import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { MarketplaceNotificationStatus } from "@redotech/redo-model/fulfillments/marketplace-notification-status";
import { toast } from "@redotech/redo-web/alert";
import {
  RedoBadge,
  RedoBadgeProps,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import {
  formatTrackingCodesWithLinks,
  getTrackingCodesWithLinks,
} from "../../common/util";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

function getStatusBadgeProps(
  status: MarketplaceNotificationStatus,
): RedoBadgeProps | null {
  if (!status) {
    return null;
  }

  switch (status.status) {
    case "pending": {
      return {
        color: "warning",
        text: status.date
          ? `Pending until ${status.date.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}`
          : "Pending",
      };
    }
    case "skipped": {
      return { color: "gray", text: "Not sent" };
    }
    case "complete": {
      return { color: "success", text: "Complete" };
    }
    case "failed": {
      return { color: "error", text: "Failed - view details" };
    }
    // No default
  }

  return null;
}

function summarizeMarketplaceNotificationStatuses(
  groups: readonly FulfillmentGroupData[],
): RedoBadgeProps | null {
  // Filter out groups without marketplace notification
  const marketplaceNotifications = groups
    .map((group) => group.marketplaceNotification)
    .filter(Boolean) as MarketplaceNotificationStatus[];

  if (
    marketplaceNotifications.length === 0 ||
    marketplaceNotifications.length !== groups.length
  ) {
    return null;
  }

  if (marketplaceNotifications.length === 1) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return getStatusBadgeProps(marketplaceNotifications[0]);
  }

  const allSameStatus = marketplaceNotifications.every(
    (status) => status?.status === marketplaceNotifications[0]?.status,
  );

  if (!allSameStatus) {
    return { color: "blue", text: "Mixed" };
  }

  // All marketplaceNotifications are the same status
  const firstNotification = marketplaceNotifications[0]!;
  if (!firstNotification.status) {
    return null;
  }

  // If they're all pending, check if they have the same date
  if (firstNotification.status === "pending") {
    const allHaveSameDate = marketplaceNotifications.every(
      (notification) =>
        notification.status === "pending" &&
        notification.date?.getTime() === firstNotification.date?.getTime(),
    );

    if (allHaveSameDate && firstNotification.date) {
      return {
        color: "warning",
        text: `Pending until ${firstNotification.date.toLocaleDateString(
          undefined,
          { weekday: "short", month: "short", day: "numeric" },
        )}`,
      };
    }

    return { color: "warning", text: "Pending" };
  }

  // Otherwise just show the correct status text
  return getStatusBadgeProps(firstNotification);
}

const MarketplaceNotificationFailedModal = memo(
  function MarketplaceNotificationFailedModal({
    open,
    onClose,
    fulfillmentGroupId,
    actionMessage,
  }: {
    open: boolean;
    onClose: () => void;
    fulfillmentGroupId: string;
    actionMessage: string;
  }) {
    const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

    const [retryLoad, doRetry] = useTriggerLoad(async (signal) => {
      void signal;
      try {
        await client.retryMarketplaceNotification({ fulfillmentGroupId });
        toast("Marketplace notification retry started", { variant: "success" });
        onClose();
      } catch {
        toast("Failed to retry marketplace notification", { variant: "error" });
      }
    });

    return (
      <RedoBaseModal isOpen={open} onModalCloseRequested={onClose}>
        <RedoModalHeader
          cancelClicked={onClose}
          title="Failed to notify marketplace"
        />
        <Flex dir="column" gap="lg" px="3xl" py="none">
          <Text fontSize="sm">
            {actionMessage ||
              "The marketplace notification failed. Please review and try again."}
          </Text>
          <Text color="secondary" fontSize="xs">
            After resolving the issue in your marketplace, click Retry to
            re-attempt the notification.
          </Text>
        </Flex>
        <Flex gap="lg" justify="flex-end" p="3xl">
          <RedoButton hierarchy="secondary" onClick={onClose} text="Cancel" />
          <RedoButton
            hierarchy="primary"
            onClick={() => doRetry()}
            pending={retryLoad.pending}
            text="Retry marketplace notification"
          />
        </Flex>
      </RedoBaseModal>
    );
  },
);

export const MarketplaceNotificationSection = memo(
  function MarketplaceNotificationSection({
    groups,
  }: {
    groups: readonly FulfillmentGroupData[];
  }) {
    const [failedModalOpen, setFailedModalOpen] = useState(false);

    const badgeProps = useMemo(
      () => summarizeMarketplaceNotificationStatuses(groups),
      [groups],
    );

    // Only a single-group selection can open the failed modal, since the modal
    // retries notification for a specific fulfillment group.
    const singleFailedGroup =
      groups.length === 1 &&
      groups[0]?.marketplaceNotification?.status === "failed"
        ? groups[0]
        : null;

    const singleFailedActionMessage =
      singleFailedGroup &&
      "actionMessage" in singleFailedGroup.marketplaceNotification!
        ? singleFailedGroup.marketplaceNotification!.actionMessage
        : undefined;

    const handleBadgeClick = useCallback(
      (e: React.MouseEvent) => {
        if (singleFailedGroup) {
          e.stopPropagation();
          setFailedModalOpen(true);
        }
      },
      [singleFailedGroup],
    );

    const handleFailedModalClose = useCallback(() => {
      setFailedModalOpen(false);
    }, []);

    // Get tracking codes with links from all groups
    const allTrackingCodesWithLinks = useMemo(() => {
      const codesWithLinks: { code: string; link: string }[] = [];
      for (const group of groups) {
        codesWithLinks.push(...getTrackingCodesWithLinks(group));
      }
      // Remove duplicates based on tracking code
      const uniqueCodes = new Map<string, string>();
      for (const { code, link } of codesWithLinks) {
        if (!uniqueCodes.has(code)) {
          uniqueCodes.set(code, link);
        }
      }
      return Array.from(uniqueCodes.entries()).map(([code, link]) => ({
        code,
        link,
      }));
    }, [groups]);

    const formattedTrackingCodes = useMemo(
      () => formatTrackingCodesWithLinks(allTrackingCodesWithLinks),
      [allTrackingCodesWithLinks],
    );

    const hasMoreTrackingCodes = allTrackingCodesWithLinks.length > 3;

    if (!badgeProps && !formattedTrackingCodes) {
      return null;
    }

    return (
      <Flex dir="column" gap="lg">
        {badgeProps && (
          <div
            onClick={handleBadgeClick}
            style={singleFailedGroup ? { cursor: "pointer" } : undefined}
          >
            <RedoBadge {...badgeProps} />
          </div>
        )}
        {singleFailedGroup && singleFailedActionMessage && (
          <MarketplaceNotificationFailedModal
            actionMessage={singleFailedActionMessage}
            fulfillmentGroupId={singleFailedGroup._id}
            onClose={handleFailedModalClose}
            open={failedModalOpen}
          />
        )}
        {formattedTrackingCodes && (
          <Flex dir="column" gap="xs">
            <Text color="secondary" fontSize="xs" fontWeight="semibold">
              Tracking code(s):
            </Text>
            <Flex gap="xs" style={{ flexWrap: "wrap" }}>
              {formattedTrackingCodes.map(({ code, link }, index) => (
                <Flex gap="none" key={code}>
                  <Text
                    color="secondary"
                    fontSize="xs"
                    onClick={() => window.open(link, "_blank")}
                    style={{
                      cursor: "pointer",
                      textDecoration: "underline",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {code}
                  </Text>
                  {index < formattedTrackingCodes.length - 1 && (
                    <Text color="secondary" fontSize="xs">
                      ,
                    </Text>
                  )}
                </Flex>
              ))}
              {hasMoreTrackingCodes && (
                <Text
                  color="secondary"
                  fontSize="xs"
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  and {allTrackingCodesWithLinks.length - 3} more
                </Text>
              )}
            </Flex>
          </Flex>
        )}
      </Flex>
    );
  },
);

export const MarketplaceNotificationBadge = memo(
  function MarketplaceNotificationBadge({
    group,
  }: {
    group: FulfillmentGroupData;
  }) {
    const [modalOpen, setModalOpen] = useState(false);

    const badgeProps = useMemo(
      () =>
        group.marketplaceNotification
          ? getStatusBadgeProps(group.marketplaceNotification)
          : null,
      [group],
    );

    const isFailed = group.marketplaceNotification?.status === "failed";

    const handleBadgeClick = useCallback(
      (e: React.MouseEvent) => {
        if (isFailed) {
          e.stopPropagation();
          setModalOpen(true);
        }
      },
      [isFailed],
    );

    const handleModalClose = useCallback(() => {
      setModalOpen(false);
    }, []);

    if (!badgeProps) {
      return null;
    }

    const actionMessage =
      isFailed && "actionMessage" in group.marketplaceNotification!
        ? group.marketplaceNotification!.actionMessage
        : undefined;

    return (
      <>
        <div
          onClick={handleBadgeClick}
          style={isFailed ? { cursor: "pointer" } : undefined}
        >
          <RedoBadge {...badgeProps} size="xs" />
        </div>
        {isFailed && actionMessage && (
          <MarketplaceNotificationFailedModal
            actionMessage={actionMessage}
            fulfillmentGroupId={group._id}
            onClose={handleModalClose}
            open={modalOpen}
          />
        )}
      </>
    );
  },
);
